"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Stepper = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const Icon_1 = require("./Icon");
const Css_1 = require("../Css");
const utils_1 = require("../utils");
const defaultTestId_1 = require("../utils/defaultTestId");
function Stepper(props) {
    const { steps, currentStep, onChange } = props;
    if (steps.length === 0) {
        throw new Error("Stepper must be initialized with at least one step");
    }
    const tid = (0, utils_1.useTestIds)(props, "stepper");
    // calc progress based on last completed step - return -1 when no steps completed
    const lastCompletedStep = steps.map((step) => step.state).lastIndexOf("complete");
    const maxStepWidth = 200;
    const minStepWidth = 100;
    const gap = 8;
    return ((0, jsx_runtime_1.jsxs)("nav", { "aria-label": "steps", css: Css_1.Css.df.fdc.w100.$, ...tid, children: [(0, jsx_runtime_1.jsx)("ol", { css: Css_1.Css.listReset.df.gapPx(gap).$, children: steps.map((step) => {
                    const isCurrent = currentStep === step.value;
                    return ((0, jsx_runtime_1.jsx)("li", { css: Css_1.Css.df.fg1.fdc.maxwPx(maxStepWidth).mwPx(minStepWidth).$, "aria-current": isCurrent, ...tid.step, children: (0, jsx_runtime_1.jsx)(StepButton, { ...step, onClick: () => onChange(step.value), isCurrent: isCurrent, ...tid.stepButton }) }, step.label));
                }) }), (0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.mt1.bgGray300
                    .hPx(4)
                    .maxwPx(steps.length * maxStepWidth + (steps.length - 1) * gap)
                    .mwPx(steps.length * minStepWidth + (steps.length - 1) * gap).w100.$, children: (0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.bgBlue600.add("transition", "width 200ms").h100.w(`${((lastCompletedStep + 1) / steps.length) * 100}%`)
                        .$ }) })] }));
}
exports.Stepper = Stepper;
function StepButton(props) {
    const { label, disabled, state, isCurrent, onClick } = props;
    const ariaProps = { onPress: onClick, isDisabled: disabled };
    const ref = (0, react_1.useRef)(null);
    const { buttonProps, isPressed } = (0, react_aria_1.useButton)(ariaProps, ref);
    const { isFocusVisible, focusProps } = (0, react_aria_1.useFocusRing)();
    const { hoverProps, isHovered } = (0, react_aria_1.useHover)(ariaProps);
    const focusRingStyles = state === "error" ? Css_1.Css.bshDanger.$ : Css_1.Css.bshFocus.$;
    const tid = (0, utils_1.useTestIds)(props, "stepButton");
    return ((0, jsx_runtime_1.jsxs)("button", { ref: ref, ...buttonProps, ...focusProps, ...hoverProps, css: {
            ...Css_1.Css.buttonBase.$,
            ...Css_1.Css.tl.w100.h100.sm.gray700.add("whiteSpace", "initial").if(state === "error").red600.$,
            ...(isCurrent ? Css_1.Css.blue700.if(state === "error").red800.$ : {}),
            ...(isHovered && !isPressed ? Css_1.Css.blue800.if(state === "error").red500.$ : {}),
            ...(isPressed ? Css_1.Css.blue500.if(state === "error").red900.$ : {}),
            ...(disabled ? Css_1.Css.gray400.cursorNotAllowed.if(state === "error").red200.$ : {}),
            ...(isFocusVisible ? focusRingStyles : {}),
        }, ...tid[(0, defaultTestId_1.defaultTestId)(label)], children: [(0, jsx_runtime_1.jsx)("span", { css: Css_1.Css.fs0.mrPx(4).$, children: (0, jsx_runtime_1.jsx)(StepIcon, { state: state, isHovered: isHovered, isPressed: isPressed, isCurrent: isCurrent }) }), label] }));
}
function StepIcon({ state, isHovered = false, isPressed = false, isCurrent = false }) {
    if (state === "error") {
        return (0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: "errorCircle" });
    }
    if (state === "complete") {
        return (0, jsx_runtime_1.jsx)(Icon_1.Icon, { icon: "check" });
    }
    // Otherwise state is "incomplete", return the custom circle "icon"
    return ((0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.wPx(24).hPx(24).df.aic.jcc.$, children: (0, jsx_runtime_1.jsx)("div", { css: Css_1.Css.wPx(10)
                .hPx(10)
                .ba.bw2.br100.add("color", "currentColor")
                .if(isHovered || isPressed || isCurrent)
                .add("backgroundColor", "currentColor").$ }) }));
}
